import {
  CategoryItemsProps,
  MenuCategoriesProps,
  Schedules,
  SelectedMenus,
  SortedItemsProps,
  TemplateContent,
} from '@zeus/shared';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(isoWeek);
dayjs.extend(isBetween);
dayjs.extend(utc);
dayjs.extend(timezone);

export const formatPhoneNumberUSA = (input: any, includeCountryCode = true) => {
  // Remove all spaces and then check for the '+1' prefix
  let cleanedInput = input.replace(/\s+/g, '');

  // Check for '+1' prefix and remove it if present
  if (cleanedInput.startsWith('+1')) {
    cleanedInput = cleanedInput.slice(2);
  }

  // Remove all non-numeric characters and limit to 10 digits
  const digitsOnly = cleanedInput.replace(/\D+/g, '').slice(0, 10);
  const area = digitsOnly.slice(0, 3);
  const prefix = digitsOnly.slice(3, 6);
  const line = digitsOnly.slice(6, 10);

  // Format based on length of digits
  let formattedPhone;
  if (digitsOnly.length <= 3) {
    formattedPhone = area;
  } else if (digitsOnly.length <= 6) {
    formattedPhone = `(${area}) ${prefix}`;
  } else {
    formattedPhone = `(${area}) ${prefix}-${line}`;
  }

  // Return formatted number with or without '+1' prefix based on includeCountryCode
  if (formattedPhone.length === 0) return '';

  return includeCountryCode ? `+1 ${formattedPhone}` : formattedPhone;
};

export const validatePhoneNumberUSA = (phoneNumber: string, includeCountryCode = true) => {
  // Define regex patterns for both scenarios
  const phonePatternWithCountryCode = /^\+1 \(\d{3}\) \d{3}-\d{4}$/; //NOSONAR
  const phonePatternWithoutCountryCode = /^\(\d{3}\) \d{3}-\d{4}$/; //NOSONAR

  // Test the input against the appropriate pattern
  if (includeCountryCode) {
    return phonePatternWithCountryCode.test(phoneNumber);
  } else {
    return phonePatternWithoutCountryCode.test(phoneNumber);
  }
};

export const getTotalItemsCount = (menus?: SelectedMenus[]) => {
  if (!menus) return 0;

  return menus?.reduce((acc: any, menu) => {
    return (
      acc +
      menu.categories.reduce((categoryAcc, category) => {
        return categoryAcc + (category?.items?.length || 0);
      }, 0)
    );
  }, 0);
};

export const getTotalCategoriesCount = (menus?: SelectedMenus[]) => {
  if (!menus) return 0;

  return menus?.reduce((acc: any, menu) => {
    return acc + menu.categories.length || 0;
  }, 0);
};

export const getAllItems = (menus?: SelectedMenus[]) => {
  if (!menus) return [];
  return menus?.flatMap((menu) => menu.categories)?.flatMap((category) => category?.items) ?? [];
};

export const sortItemsByModifiers = (items?: CategoryItemsProps[], validModifiers: string[] = []) => {
  if (!items) return [];

  return [...items].sort((a, b) => {
    // Check if items have modifiers matching the validModifiers
    const hasValidModifiersA = Object.keys(a?.modifiers ?? {}).some((key) => validModifiers.includes(key)) ? 1 : 0;
    const hasValidModifiersB = Object.keys(b?.modifiers ?? {}).some((key) => validModifiers.includes(key)) ? 1 : 0;

    // Sort based on the presence of valid modifiers
    return hasValidModifiersB - hasValidModifiersA;
  });
};

export const getAllCategoriesAndItems = (menus: SelectedMenus[], limit: number) => {
  let itemCount = 0;
  const result: { id: number; name: string; items?: CategoryItemsProps[] }[] = [];

  for (const menu of menus) {
    for (const category of menu.categories) {
      const remainingItems = limit - itemCount;
      if (remainingItems <= 0) break;

      const itemsToAdd = category?.items?.slice(0, remainingItems);
      result.push({
        id: category.id,
        name: category.name,
        items: itemsToAdd,
      });

      itemCount += itemsToAdd?.length ?? 0;
      if (itemCount >= limit) break;
    }
    if (itemCount >= limit) break;
  }
  return result.filter((category) => category.items && category?.items?.length > 0);
};

export const hasNonEmptyModifiers = (content: TemplateContent[]) => {
  if (!content) return false;

  return content.some((menu) =>
    menu.categories.some((category) =>
      category?.items?.some((item) => item.modifiers && Object.keys(item.modifiers).length > 0)
    )
  );
};

export const categoryHasAnyModifiers = (category?: MenuCategoriesProps) => {
  if (!category) return false;

  return category?.items?.some((item) => item.modifiers && Object.keys(item.modifiers).length > 0);
};

export const getLastItemIndexOfCategory = (items?: SortedItemsProps[] | null) => {
  if (!items) return;

  const itemIndex = items
    ?.map((item, itemIndex) => (item.hasValidModifiers ? itemIndex : -1))
    .filter((index) => index !== -1) // Filter out invalid indexes
    .pop();

  return itemIndex;
};

export const getCategoriesAndItems = (menus: SelectedMenus[]) => {
  return menus.flatMap((menu) =>
    menu.categories.map((category) => ({
      id: category.id,
      name: category.name,
      items: category?.items?.map((item) => ({ ...item })),
    }))
  );
};

export const getAllMenusWithCategoriesAndItems = (menus: SelectedMenus[], limit: number) => {
  let itemCount = 0;

  const result: {
    id: number;
    name: string;
    categories: { id: number; name: string; items?: CategoryItemsProps[] }[];
    schedules?: Schedules[] | null;
  }[] = [];

  for (const menu of menus) {
    const remainingItems = limit - itemCount;
    if (remainingItems <= 0) break;

    const menuCategories: { id: number; name: string; items?: CategoryItemsProps[]; schedules?: Schedules[] | null }[] =
      [];

    for (const category of menu.categories) {
      const remainingItemsInCategory = limit - itemCount;
      if (remainingItemsInCategory <= 0) break;

      const itemsToAdd = category.items?.slice(0, remainingItemsInCategory) || [];
      if (itemsToAdd.length > 0) {
        menuCategories.push({
          id: category.id,
          name: category.name,
          items: itemsToAdd,
          schedules: category.schedules,
        });
        itemCount += itemsToAdd.length;
      }

      if (itemCount >= limit) break;
    }

    if (menuCategories.length > 0) {
      result.push({
        id: menu.id,
        name: menu.name,
        categories: menuCategories,
        schedules: menu.schedules,
      });
    }

    if (itemCount >= limit) break;
  }

  return result;
};

export const getTemplate6Items = (
  category: MenuCategoriesProps | null,
  itemsLimit: number,
  validModifiers: string[] = [],
  timezone?: string,
  development?: boolean
) => {
  const items = category?.items;
  if (Array.isArray(items) && items.length > 0) {
    const visibilityItems = development
      ? items
      : items.filter((item) => {
          if (isBetweenRange(item.schedules, timezone)) {
            return item;
          }
        });
    return processItemsWithModifiers(visibilityItems, validModifiers);
  }

  return [];
};

const convertDayToDate = (day: string, startTime: string, endTime: string, timezone?: string) => {
  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Find the target day index (0 = Sunday, 6 = Saturday)
  const targetDayIndex = daysOfWeek.indexOf(day);
  if (targetDayIndex === -1) throw new Error(`Invalid day: ${day}`);

  // Get the current date and determine the day difference
  const today = dayjs();
  const currentDayIndex = today.day(); // Day index for today
  const diff = targetDayIndex - currentDayIndex;

  // Calculate the target date
  const targetDate = today.add(diff, 'day');

  // Create start and end Date objects
  const startDateTime = dayjs.tz(`${targetDate.format('YYYY-MM-DD')}T${startTime}`, timezone).toDate();
  const endDateTime = dayjs.tz(`${targetDate.format('YYYY-MM-DD')}T${endTime}`, timezone).toDate();

  return { startDateTime, endDateTime };
};

export const isBetweenRange = (schedules?: Schedules[] | null, timezone?: string) => {
  const getCurrentDay = () => dayjs().tz(timezone).format('ddd');
  const currentDay = getCurrentDay();

  if (!schedules) return null;

  return (
    schedules &&
    schedules.some(({ day, startTime, endTime }) => {
      if (day !== currentDay) return false;

      const { startDateTime, endDateTime } = convertDayToDate(day, startTime, endTime, timezone);

      const now = dayjs().tz(timezone);

      return now.isBetween(dayjs(startDateTime), dayjs(endDateTime), null, '[]');
    })
  );
};

export const hasCatgoryNonEmptyModifiers = (category: MenuCategoriesProps | null) => {
  if (!category) return false;

  return category?.items?.some((item) => item.modifiers && Object.keys(item.modifiers).length > 0);
};

export const processItemsWithModifiers = (items?: CategoryItemsProps[], validModifiers: string[] = []) => {
  if (!items) return [];

  const processedItems = items.map((item) => {
    const hasValidModifiers = Object.keys(item.modifiers || {}).some((key) => validModifiers.includes(key));

    return { ...item, hasValidModifiers };
  });

  const sortedItems = processedItems.sort((a, b) => Number(b.hasValidModifiers) - Number(a.hasValidModifiers));

  return sortedItems;
};

export const getScaleSize = (number: number, scale: number) => {
  return `${number * scale}px`;
};

export function createSlug(name: string, itemId: number | null | undefined) {
  if (name && itemId) {
    // Convert name to lowercase
    let slug = name.toLowerCase();

    // Replace spaces with hyphens
    slug = slug.replace(/\s+/g, '-');

    // Remove any special characters except hyphens
    slug = slug.replace(/[^a-z0-9-]/g, '');

    // Append the item ID at the end
    slug = `${slug}-${itemId}`;

    return slug;
  }
}

export const getLimitedItems = (data: SelectedMenus | null, itemLimit: number) => {
  let itemCount = 0;
  const categories = [];

  if (!data) return [];

  for (const category of data.categories) {
    const limitedItems = [];

    // eslint-disable-next-line no-unsafe-optional-chaining
    for (const item of category?.items as CategoryItemsProps[]) {
      if (itemCount >= itemLimit) break; // Stop adding items but continue outer loop

      limitedItems.push(item);
      itemCount++;
    }

    if (limitedItems.length > 0) {
      categories.push({ ...category, items: limitedItems });
    }

    if (itemCount >= itemLimit) break;
  }

  return categories;
};

export const getActiveCategories = (menu?: SelectedMenus | null, timezone?: string, development?: boolean) => {
  if (Array.isArray(menu?.categories) && menu?.categories && menu?.categories?.length > 0) {
    const activeCategories = development
      ? menu?.categories.map((category) => ({ ...category, isActive: true }))
      : menu?.categories
          .filter((category) => isBetweenRange(category.schedules, timezone))
          .map((category) => ({ ...category, isActive: true }));

    return activeCategories;
  }

  return [];
};

export const truncateText = (text: string, maxLength: number) => {
  return text?.length > maxLength ? text?.slice(0, maxLength) + '…' : text;
};

export const getImageSrc = (img: string | { src: string }): string => {
  return typeof img === 'string' ? img : img.src;
};

/**
 * Extracts font family name from a Google Fonts URL or font URL
 * @param fontUrl - The font URL (e.g., 'https://fonts.googleapis.com/css2?family=Manrope:wght@200..800&display=swap')
 * @param fallback - Fallback font family (default: 'sans-serif')
 * @returns Font family string (e.g., 'Manrope, sans-serif')
 */
export const extractFontFamilyFromUrl = (fontUrl?: string, fallback = 'sans-serif'): string => {
  if (!fontUrl) return fallback;

  try {
    // Handle Google Fonts URL format: https://fonts.googleapis.com/css2?family=FontName:params&display=swap
    if (fontUrl.includes('fonts.googleapis.com')) {
      const url = new URL(fontUrl);
      const familyParam = url.searchParams.get('family');

      if (familyParam) {
        // Extract font name (everything before the colon)
        const fontName = familyParam.split(':')[0];
        // Replace + with spaces and decode
        const decodedName = decodeURIComponent(fontName.replace(/\+/g, ' '));
        return `${decodedName}, ${fallback}`;
      }
    }

    return fallback;
  } catch (error) {
    console.error('Error extracting font family from URL:', error);
    return fallback;
  }
};

// Export builder utilities
export * from './builderUtils';
